import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Paint } from './paintStyle';
import type { GiftLedgerView, MembershipStatus, TimelineStretch } from '../../functions/utils/membershipStatus';

/** Wash visual for an atmosphere, consumable by AtmosphereChatWash. */
export interface AtmosphereWash {
  baseColor: string;
  baseLayers?: string;
  image?: string;
  layers?: string;
  layers2?: string;
  chatEdge: string;
}

/** One profile-theme picker entry (see functions/utils/supabase.ts). */
export interface AtmosphereOption {
  id: string;
  name: string;
  kind: string | null;
  swatch: string;
  accent: string;
  chat_frost: boolean;
  unlocked: boolean;
  unlock_note: string | null;
  wash: AtmosphereWash | null;
  /** R2 asset URLs for the cologne-chrome renderer; null for wash atmospheres. */
  chrome: { texture: string; coin: string | null; frame: string | null } | null;
}

export interface MeResponse {
  twitch_user_id: string;
  login: string;
  display_name: string;
  avatar_url: string;
  user_number: number | null;
  cosmetics: Array<{
    slug: string;
    name: string;
    description: string | null;
    animated: boolean;
    asset_path: string;
    granted_at: string | null;
    is_default: boolean;
  }>;
  profile_theme: {
    theme: string;
    atmospheres: AtmosphereOption[];
    can_paint: boolean;
    is_supporter: boolean;
    ever_subscribed: boolean;
    active_subscriber: boolean;
  };
  /** Optional: an older Functions deployment does not send it. */
  profile_prefs?: {
    hidden_sections: string[];
    chat_atmosphere: boolean;
    include_twitch_recap: boolean;
    recap_hours: number;
  };
  seventv_paint: Paint | null;
  active_cosmetic: string | null;
  subscription: {
    status: 'active' | 'past_due' | 'canceled' | 'inactive' | 'unknown';
    /** Where active status comes from: a paid Stripe sub, an admin gift, or none. */
    source?: 'stripe' | 'paypal' | 'gift' | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    total_months: number;
    /** First-ever paid month, the lifetime "member since" anchor. */
    first_subscribed_at?: string | null;
    last_paid_at: string | null;
    stripe_customer_id: string | null;
    paypal_subscription_id?: string | null;
    /** True only for a permanent gift (no end date). */
    gift_permanent?: boolean;
    /** How a live paid subscription bills; null (or absent on an older deploy) without one. */
    billing_interval?: 'month' | 'year' | null;
  } | null;
  /** Paid or gifted, and when billing starts again. Optional: an older
   *  Functions deployment does not send it. */
  membership_status?: MembershipStatus;
  /** Every gift received, newest first, with its gifter. Optional: an older
   *  Functions deployment does not send it. */
  gifts?: GiftLedgerView[];
  /** What covers the member from now on, one stretch per kind, in order.
   *  Optional: an older Functions deployment does not send it. */
  membership_timeline?: TimelineStretch[];
  /** True only for the configured admin Twitch id. Drives whether the SPA
   *  shows the /admin link; real authz is enforced server-side. */
  is_admin?: boolean;
}

interface AuthContextValue {
  me: MeResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  login: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Cache the last-known signed-in user so a returning visitor renders as logged
// in immediately while /api/me revalidates in the background. The HttpOnly
// session cookie stays the source of truth; this only removes the logged-out
// flash and survives transient /api/me failures.
const ME_CACHE_KEY = 'sn_me_cache_v1';

function readCachedMe(): MeResponse | null {
  try {
    const raw = localStorage.getItem(ME_CACHE_KEY);
    return raw ? (JSON.parse(raw) as MeResponse) : null;
  } catch {
    return null;
  }
}

function writeCachedMe(me: MeResponse | null): void {
  try {
    if (me) localStorage.setItem(ME_CACHE_KEY, JSON.stringify(me));
    else localStorage.removeItem(ME_CACHE_KEY);
  } catch {
    /* private mode / quota — cache is best-effort */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(() => readCachedMe());
  // If we have a cached user, render it right away instead of blocking on /api/me.
  const [loading, setLoading] = useState(() => readCachedMe() === null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch('/api/me', { credentials: 'include' });
      // Only a real 401 means the session is gone — clear and log out.
      if (res.status === 401) {
        setMe(null);
        writeCachedMe(null);
        return;
      }
      // Any other non-OK (a Supabase 404/5xx, a blip) is transient: keep the
      // cached session rather than bouncing the user to the sign-in button.
      if (!res.ok) throw new Error(`me: ${res.status}`);
      const data = (await res.json()) as MeResponse;
      setMe(data);
      writeCachedMe(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setMe(null);
    writeCachedMe(null);
  }, []);

  const login = useCallback(() => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const url = `/api/auth/twitch/start?return_to=${encodeURIComponent(returnTo)}`;
    window.location.assign(url);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ me, loading, error, refresh, logout, login }),
    [me, loading, error, refresh, logout, login],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
